import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axiosInstance from "../services/axiosInstance";
import { socket } from "../services/socket";
import { useAuth } from "../hooks/useAuth";

const NotificationContext = createContext();

export function NotificationProvider({ children }) {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const { isAuthenticated, user } = useAuth();
    const isAdmin = user?.user_type === 1;
    const syncIntervalRef = useRef(null);
    const socketListenersRef = useRef(false);

    const fetchNotifications = useCallback(async () => {
        // ✅ GUARD: Don't fetch if not authenticated
        if (!isAuthenticated() || !isAdmin) {
            setNotifications([]);
            return;
        }

        setLoading(true);
        try {
            const currentToken = localStorage.getItem("token") || sessionStorage.getItem("token");

            // ✅ GUARD: If no token, skip fetch
            if (!currentToken || currentToken === 'null' || currentToken === 'undefined') {
                setNotifications([]);
                setLoading(false);
                return;
            }

            const res = await axiosInstance.get("/admin/notifications", {
                headers: { Authorization: `Bearer ${currentToken}` },
            });
            setNotifications(res.data || []);
        } catch (e) {
            // ✅ GUARD: Only log non-auth errors
            if (e.response?.status !== 401 && e.response?.status !== 403) {
                console.error('Error fetching notifications:', e);
            }
            // On auth error, clear notifications (user will be logged out)
            if (e.response?.status === 401 || e.response?.status === 403) {
                setNotifications([]);
            }
        } finally {
            setLoading(false);
        }
    }, [isAuthenticated, isAdmin]);

    useEffect(() => {
        // ✅ GUARD: Only fetch and listen if user is authenticated
        if (!isAuthenticated() || !isAdmin) {
            // Clear everything on logout
            setNotifications([]);
            // Remove socket listeners if they were set up
            if (socketListenersRef.current) {
                socket.off("notification_new");
                socket.off("notification_read");
                socketListenersRef.current = false;
            }
            // Clear sync interval on logout
            if (syncIntervalRef.current) {
                clearInterval(syncIntervalRef.current);
                syncIntervalRef.current = null;
            }
            return;
        }

        // ✅ Fetch notifications on authentication
        fetchNotifications();

        // Get current user's customer_id from token
        const getCustomerIdFromToken = () => {
            const currentToken = localStorage.getItem("token") || sessionStorage.getItem("token");
            if (!currentToken) return null;
            try {
                const payload = JSON.parse(atob(currentToken.split('.')[1]));
                return payload.customer_id;
            } catch (e) {
                console.error('Failed to decode token:', e);
                return null;
            }
        };

        const userCustomerId = getCustomerIdFromToken();

        // ✅ GUARD: Only set up socket listeners if we have a valid customer_id
        if (userCustomerId && !socketListenersRef.current) {
            socketListenersRef.current = true;

            // ✅ CRITICAL: Only accept notifications for THIS customer
            socket.on("notification_new", notif => {
                if (notif && notif.customerId === userCustomerId) {
                    // Check if notification already exists (deduplication)
                    setNotifications(prev => {
                        const exists = prev.some(n => n.id === notif.id);
                        if (!exists) {
                            console.log('📬 New notification received:', notif);
                            return [notif, ...prev];
                        }
                        return prev;
                    });
                    // Fetch fresh from DB to ensure we have the saved notification with ID
                    setTimeout(() => {
                        fetchNotifications();
                    }, 500);
                }
            });

            socket.on("notification_read", ({ id }) => {
                setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
            });

            socket.on("notification_read_all", ({ ids }) => {
                if (!Array.isArray(ids) || ids.length === 0) return;
                setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n));
            });
        }

        // ✅ Periodic sync every 60 seconds (safety net for missed socket events)
        if (!syncIntervalRef.current) {
            syncIntervalRef.current = setInterval(() => {
                if (isAuthenticated() && isAdmin) {
                    fetchNotifications();
                }
            }, 60000);
        }

        return () => {
            // Cleanup is handled by the dependency array - when isAuthenticated changes, this effect runs again
            // The guard at the top will handle cleanup
        };
    }, [isAuthenticated, isAdmin, fetchNotifications]);

    const markAsRead = async (id) => {
        // ✅ GUARD: Only allow if authenticated
        if (!isAuthenticated() || !isAdmin) {
            console.warn('Cannot mark notification as read - user not authenticated');
            return;
        }

        try {
            const currentToken = localStorage.getItem("token") || sessionStorage.getItem("token");
            if (!currentToken || currentToken === 'null' || currentToken === 'undefined') {
                return;
            }

            await axiosInstance.patch(`/admin/notifications/${id}/read`, {}, {
                headers: { Authorization: `Bearer ${currentToken}` },
            });
            setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
        } catch (e) {
            // ✅ Silently fail on auth errors - user will be logged out
            if (e.response?.status !== 401 && e.response?.status !== 403) {
                console.error('Error marking notification as read:', e);
            }
        }
    };

    const markAllAsRead = async () => {
        // ✅ GUARD: Only allow if authenticated
        if (!isAuthenticated() || !isAdmin) {
            console.warn('Cannot mark notifications as read - user not authenticated');
            return;
        }

        try {
            const currentToken = localStorage.getItem("token") || sessionStorage.getItem("token");
            if (!currentToken || currentToken === 'null' || currentToken === 'undefined') {
                return;
            }

            const res = await axiosInstance.patch(`/admin/notifications/read-all`, {}, {
                headers: { Authorization: `Bearer ${currentToken}` },
            });
            const ids = res.data?.ids || [];
            if (ids.length) {
                setNotifications(prev => prev.map(n => ids.includes(n.id) ? { ...n, read: true } : n));
            } else {
                setNotifications(prev => prev.map(n => ({ ...n, read: true })));
            }
        } catch (e) {
            if (e.response?.status !== 401 && e.response?.status !== 403) {
                console.error('Error marking all notifications as read:', e);
            }
        }
    };

    return (
        <NotificationContext.Provider value={{ notifications, loading, fetchNotifications, markAsRead, markAllAsRead }}>
            {children}
        </NotificationContext.Provider>
    );
}

export function useNotifications() {
    return useContext(NotificationContext);
}
